/**
 * CSV Utilities
 *
 * Functions for parsing and serializing CSV data for QuickFill table
 */

export interface CSVParseOptions {
  delimiter?: string;
  hasHeader?: boolean;
  skipEmptyLines?: boolean;
}

export interface CSVExportOptions {
  delimiter?: string;
  includeHeader?: boolean;
  columns?: string[];
}

export interface ImportPreview {
  headers: string[];
  rows: string[][];
  totalRows: number;
  matchedRows: number;
  newRows: number;
  warnings: string[];
}

export interface ImportResult {
  success: boolean;
  updatedCount: number;
  skippedCount: number;
  errors: string[];
}

/**
 * Parse CSV string into array of objects
 */
export function parseCSV(
  csvString: string,
  options: CSVParseOptions = {}
): { headers: string[]; data: Record<string, string>[] } {
  const {
    delimiter = ',',
    hasHeader = true,
    skipEmptyLines = true
  } = options;

  // Split into lines and handle different line endings
  let lines = csvString.split(/\r?\n/);

  // Skip empty lines if requested
  if (skipEmptyLines) {
    lines = lines.filter(line => line.trim() !== '');
  }

  if (lines.length === 0) {
    return { headers: [], data: [] };
  }

  // Parse a CSV line, handling quoted values
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Don't forget the last field
    result.push(current.trim());

    return result;
  };

  // Get headers
  const headers = hasHeader ? parseLine(lines[0]) : [];
  const dataLines = hasHeader ? lines.slice(1) : lines;

  // Parse data rows
  const data: Record<string, string>[] = [];

  for (const line of dataLines) {
    const values = parseLine(line);

    if (hasHeader) {
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    } else {
      // Use numeric indices as keys
      const row: Record<string, string> = {};
      values.forEach((value, index) => {
        row[`col${index}`] = value;
      });
      data.push(row);
    }
  }

  return { headers, data };
}

/**
 * Serialize array of objects to CSV string
 */
export function serializeToCSV(
  data: Record<string, unknown>[],
  options: CSVExportOptions = {}
): string {
  const {
    delimiter = ',',
    includeHeader = true,
    columns
  } = options;

  if (data.length === 0) {
    return '';
  }

  // Determine columns to include
  const cols = columns || Object.keys(data[0]);

  // Escape a value for CSV
  const escapeValue = (value: unknown): string => {
    const str = value === null || value === undefined ? '' : String(value);

    // Check if we need to quote the value
    const needsQuoting = str.includes(delimiter) ||
      str.includes('"') ||
      str.includes('\n') ||
      str.includes('\r');

    if (needsQuoting) {
      // Escape quotes by doubling them
      const escaped = str.replace(/"/g, '""');
      return `"${escaped}"`;
    }

    return str;
  };

  const lines: string[] = [];

  // Add header row
  if (includeHeader) {
    lines.push(cols.map(escapeValue).join(delimiter));
  }

  // Add data rows
  for (const row of data) {
    const values = cols.map(col => escapeValue(row[col]));
    lines.push(values.join(delimiter));
  }

  return lines.join('\n');
}

/**
 * Generate preview of CSV import
 */
export function previewCSVImport(
  csvString: string,
  existingNames: Set<string>,
  options: CSVParseOptions = {}
): ImportPreview {
  const { headers, data } = parseCSV(csvString, options);
  const warnings: string[] = [];

  // Check if Name column exists
  const hasNameColumn = headers.includes('Name');
  if (!hasNameColumn) {
    warnings.push('CSV does not contain a "Name" column. Rows cannot be matched to existing data.');
  }

  // Count matched and new rows
  let matchedRows = 0;
  let newRows = 0;

  if (hasNameColumn) {
    for (const row of data) {
      const name = row['Name'];
      if (name && existingNames.has(name)) {
        matchedRows++;
      } else if (name) {
        newRows++;
      }
    }
  }

  // Check for empty rows
  const emptyRows = data.filter(row => {
    return Object.values(row).every(v => !v || v.trim() === '');
  }).length;

  if (emptyRows > 0) {
    warnings.push(`${emptyRows} empty rows will be skipped.`);
  }

  // Convert data back to string arrays for preview
  const rows = data.slice(0, 10).map(row => headers.map(h => row[h] || ''));

  return {
    headers,
    rows,
    totalRows: data.length,
    matchedRows,
    newRows,
    warnings
  };
}

/**
 * Download CSV content as a file
 */
export function downloadCSV(csvContent: string, filename: string = 'export.csv'): void {
  // Add BOM for Excel compatibility with UTF-8
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Read CSV file and return content as string
 */
export function readCSVFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        resolve(content);
      } else {
        reject(new Error('Failed to read file content'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Merge imported CSV data with existing data
 * - Matches by Name column
 * - Updates existing rows with new values (non-empty only)
 * - Does NOT add new rows (data comes from Revit)
 */
export function mergeCSVData<T extends Record<string, unknown>>(
  existingData: T[],
  importedData: Record<string, string>[],
  columnsToUpdate: string[]
): { updatedData: T[]; result: ImportResult } {
  const errors: string[] = [];
  let updatedCount = 0;
  let skippedCount = 0;

  // Create map of existing data by Name
  const existingMap = new Map<string, number>();
  existingData.forEach((item, index) => {
    const name = item['Name'] as string;
    if (name) {
      existingMap.set(name, index);
    }
  });

  // Clone existing data
  const updatedData = existingData.map(item => ({ ...item }));

  // Process imported rows
  for (const importRow of importedData) {
    const name = importRow['Name'];

    if (!name) {
      skippedCount++;
      continue;
    }

    const existingIndex = existingMap.get(name);

    if (existingIndex === undefined) {
      // Row doesn't exist in current data - skip (data comes from Revit)
      skippedCount++;
      continue;
    }

    // Update existing row with imported values
    let hasUpdates = false;
    const existingItem = updatedData[existingIndex];

    for (const col of columnsToUpdate) {
      if (col === 'Name') continue; // Don't update Name

      const importValue = importRow[col];

      // Only update if imported value is not empty
      if (importValue !== undefined && importValue !== '') {
        (existingItem as Record<string, unknown>)[col] = importValue;
        hasUpdates = true;
      }
    }

    if (hasUpdates) {
      updatedCount++;
    }
  }

  return {
    updatedData,
    result: {
      success: errors.length === 0,
      updatedCount,
      skippedCount,
      errors
    }
  };
}
